import { useRef, useState, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import DOMPurify from "dompurify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link,
  Image,
  Youtube,
  Code,
  Quote,
  Minus,
  Undo,
  Redo,
  Type,
  Upload,
  Loader2,
  Eye,
  Pencil,
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export const RichTextEditor = ({ value, onChange, placeholder }: RichTextEditorProps) => {
  const { t } = useLanguage();
  const editorRef = useRef<HTMLDivElement>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoPopoverOpen, setVideoPopoverOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const execCommand = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  }, []);

  // Custom hook to sanitize iframe sources - only allow trusted video providers
  const sanitizeIframeSrc = (node: Element) => {
    if (node.tagName === 'IFRAME') {
      const src = node.getAttribute('src') || '';
      const trustedDomains = [
        'www.youtube-nocookie.com',
        'youtube-nocookie.com',
        'www.youtube.com',
        'youtube.com',
        'player.vimeo.com',
        'vimeo.com'
      ];
      const isTrusted = trustedDomains.some(domain => {
        try {
          const url = new URL(src);
          return url.hostname === domain || url.hostname.endsWith('.' + domain);
        } catch {
          return false;
        }
      });
      if (!isTrusted) {
        node.remove();
        return false;
      }
    }
    return true;
  };

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      // Configure DOMPurify to filter iframes by source
      DOMPurify.addHook('uponSanitizeElement', (node) => {
        if (node instanceof Element) {
          sanitizeIframeSrc(node);
        }
      });
      
      // Sanitize HTML content to prevent XSS attacks
      const sanitized = DOMPurify.sanitize(editorRef.current.innerHTML, {
        ALLOWED_TAGS: ['p', 'br', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'strong', 'em', 'u', 's', 'code', 'pre', 'blockquote', 'hr', 'img', 'iframe'],
        ALLOWED_ATTR: ['src', 'href', 'class', 'alt', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen', 'target', 'rel', 'style'],
        ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      });
      
      // Remove the hook after use to prevent affecting other sanitization calls
      DOMPurify.removeHook('uponSanitizeElement');
      
      onChange(sanitized);
    }
  }, [onChange]);

  const handleHeadingChange = (heading: string) => {
    if (heading === "p") {
      execCommand("formatBlock", "<p>");
    } else {
      execCommand("formatBlock", `<${heading}>`);
    }
  };

  const insertLink = () => {
    if (linkUrl) {
      execCommand("createLink", linkUrl);
      setLinkUrl("");
      setLinkPopoverOpen(false);
    }
  };

  const insertImage = () => {
    if (imageUrl) {
      execCommand("insertHTML", `<img src="${imageUrl}" alt="Image" class="max-w-full h-auto rounded-lg my-4" />`);
      setImageUrl("");
      setImagePopoverOpen(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("richEditor.invalidImageType"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("richEditor.imageTooLarge"));
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `lesson-content/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("lesson-images")
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("lesson-images")
        .getPublicUrl(filePath);

      execCommand("insertHTML", `<img src="${publicUrl}" alt="${file.name}" class="max-w-full h-auto rounded-lg my-4" />`);
      toast.success(t("richEditor.imageUploaded"));
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(t("richEditor.uploadFailed"));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const insertVideo = () => {
    if (videoUrl) {
      let embedUrl = videoUrl;
      
      // Convert YouTube watch URL to embed URL
      if (videoUrl.includes("youtube.com/watch")) {
        const videoId = videoUrl.split("v=")[1]?.split("&")[0];
        if (videoId) {
          embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
        }
      } else if (videoUrl.includes("youtu.be/")) {
        const videoId = videoUrl.split("youtu.be/")[1]?.split("?")[0];
        if (videoId) {
          embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}`;
        }
      }

      execCommand("insertHTML", `
        <div class="video-embed my-4">
          <iframe 
            src="${embedUrl}" 
            width="100%" 
            height="400" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen
            class="rounded-lg"
          ></iframe>
        </div>
      `);
      setVideoUrl("");
      setVideoPopoverOpen(false);
    }
  };

  const insertCodeBlock = () => {
    execCommand("insertHTML", `<pre class="bg-muted p-4 rounded-lg overflow-x-auto my-4"><code>// Code here</code></pre>`);
  };

  const insertBlockquote = () => {
    execCommand("formatBlock", "<blockquote>");
  };

  const insertHorizontalRule = () => {
    execCommand("insertHTML", "<hr class='my-6 border-border' />");
  };

  const ToolbarButton = ({ 
    onClick, 
    icon: Icon, 
    title,
    active = false 
  }: { 
    onClick: () => void; 
    icon: React.ElementType; 
    title: string;
    active?: boolean;
  }) => (
    <Button
      type="button"
      variant={active ? "secondary" : "ghost"}
      size="sm"
      onClick={onClick}
      title={title}
      className="h-8 w-8 p-0"
    >
      <Icon className="h-4 w-4" />
    </Button>
  );

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-border bg-muted/30">
        {/* Heading Select */}
        <Select onValueChange={handleHeadingChange} defaultValue="p">
          <SelectTrigger className="w-[100px] h-8">
            <Type className="h-4 w-4 mr-1" />
            <SelectValue placeholder={t("richEditor.heading")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="p">{t("richEditor.paragraph")}</SelectItem>
            <SelectItem value="h1">H1</SelectItem>
            <SelectItem value="h2">H2</SelectItem>
            <SelectItem value="h3">H3</SelectItem>
            <SelectItem value="h4">H4</SelectItem>
          </SelectContent>
        </Select>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Text Formatting */}
        <ToolbarButton onClick={() => execCommand("bold")} icon={Bold} title={t("richEditor.bold")} />
        <ToolbarButton onClick={() => execCommand("italic")} icon={Italic} title={t("richEditor.italic")} />
        <ToolbarButton onClick={() => execCommand("underline")} icon={Underline} title={t("richEditor.underline")} />
        <ToolbarButton onClick={() => execCommand("strikeThrough")} icon={Strikethrough} title={t("richEditor.strikethrough")} />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Lists */}
        <ToolbarButton onClick={() => execCommand("insertUnorderedList")} icon={List} title={t("richEditor.bulletList")} />
        <ToolbarButton onClick={() => execCommand("insertOrderedList")} icon={ListOrdered} title={t("richEditor.numberedList")} />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Alignment */}
        <ToolbarButton onClick={() => execCommand("justifyLeft")} icon={AlignLeft} title={t("richEditor.alignLeft")} />
        <ToolbarButton onClick={() => execCommand("justifyCenter")} icon={AlignCenter} title={t("richEditor.alignCenter")} />
        <ToolbarButton onClick={() => execCommand("justifyRight")} icon={AlignRight} title={t("richEditor.alignRight")} />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Link */}
        <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" title={t("richEditor.insertLink")}>
              <Link className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <Label>{t("richEditor.linkUrl")}</Label>
              <Input
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://..."
                onKeyDown={(e) => e.key === "Enter" && insertLink()}
              />
              <Button onClick={insertLink} size="sm" className="w-full">
                {t("richEditor.insert")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Image */}
        <Popover open={imagePopoverOpen} onOpenChange={setImagePopoverOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" title={t("richEditor.insertImage")}>
              <Image className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <Label>{t("richEditor.imageUrl")}</Label>
              <Input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                onKeyDown={(e) => e.key === "Enter" && insertImage()}
              />
              <Button onClick={insertImage} size="sm" className="w-full">
                {t("richEditor.insert")}
              </Button>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-popover px-2 text-muted-foreground">{t("richEditor.or")}</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {t("richEditor.uploadImage")}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
          </PopoverContent>
        </Popover>

        {/* Video */}
        <Popover open={videoPopoverOpen} onOpenChange={setVideoPopoverOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0" title={t("richEditor.insertVideo")}>
              <Youtube className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80">
            <div className="space-y-3">
              <Label>{t("richEditor.videoUrl")}</Label>
              <Input
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
                onKeyDown={(e) => e.key === "Enter" && insertVideo()}
              />
              <p className="text-xs text-muted-foreground">
                {t("richEditor.videoHint")}
              </p>
              <Button onClick={insertVideo} size="sm" className="w-full">
                {t("richEditor.insert")}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Special Blocks */}
        <ToolbarButton onClick={insertCodeBlock} icon={Code} title={t("richEditor.codeBlock")} />
        <ToolbarButton onClick={insertBlockquote} icon={Quote} title={t("richEditor.blockquote")} />
        <ToolbarButton onClick={insertHorizontalRule} icon={Minus} title={t("richEditor.horizontalRule")} />

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* Undo/Redo */}
        <ToolbarButton onClick={() => execCommand("undo")} icon={Undo} title={t("richEditor.undo")} />
        <ToolbarButton onClick={() => execCommand("redo")} icon={Redo} title={t("richEditor.redo")} />

        {/* Preview Toggle */}
        <div className="flex-1" />
        <Button
          type="button"
          variant={isPreviewMode ? "secondary" : "ghost"}
          size="sm"
          onClick={() => setIsPreviewMode(!isPreviewMode)}
          className="h-8"
        >
          {isPreviewMode ? (
            <>
              <Pencil className="h-4 w-4 mr-1" />
              {t("richEditor.edit")}
            </>
          ) : (
            <>
              <Eye className="h-4 w-4 mr-1" />
              {t("richEditor.preview")}
            </>
          )}
        </Button>
      </div>

      {/* Editor / Preview Area */}
      {isPreviewMode ? (
        <div 
          className="p-4 min-h-[300px] prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) || `<p class="text-muted-foreground">${placeholder || ""}</p>` }}
        />
      ) : (
        <div
          ref={editorRef}
          contentEditable
          className="p-4 min-h-[300px] focus:outline-none prose prose-sm dark:prose-invert max-w-none
            [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:mb-4
            [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-3
            [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mb-2
            [&_h4]:text-lg [&_h4]:font-medium [&_h4]:mb-2
            [&_p]:mb-3
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
            [&_li]:mb-1
            [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4
            [&_a]:text-primary [&_a]:underline
            [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto
            [&_code]:font-mono [&_code]:text-sm
            [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg
          "
          onInput={handleContentChange}
          onBlur={handleContentChange}
          dangerouslySetInnerHTML={{ __html: value || "" }}
          data-placeholder={placeholder}
        />
      )}
    </div>
  );
};
